import React, { useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, TextInput, Pressable, ActivityIndicator } from '../../lib/react-native-compat';
import { userNameAtom } from '../../store/company';
import { uploadFieldReportPhoto, deleteFieldReportPhoto } from '../../db/company';
import { showToast } from '../../helpers/toast';
import { fieldReportAtom } from './state';
import { readExifGps } from './exif';

// Photos step: pick/capture → caption → upload. Client downscales to max
// 1600px JPEG before upload (field bandwidth matters more than fidelity).
// Uploads are immediate (separate from the JSON report save).

const MAX_DIM = 1600;

// Shared with SubDocsUpload (sub time tickets shot as photos downscale the
// same way; PDFs skip this).
export const downscale = (file) =>
    new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
            if (scale >= 1 && file.type === 'image/jpeg') {
                resolve(file);
                return;
            }
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.8);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file); // let the server keep the original if we can't decode
        };
        img.src = url;
    });

const inputBase = {
    minHeight: 44,
    fontSize: 16,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 12,
    paddingRight: 12,
    width: '100%',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
};

// uploadOnly: render just the add button + pending capture cards (the Review
// page shows the uploaded set itself, in its own grid).
const PhotosStep = ({ readOnly, uploadOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [userName] = useAtom(userNameAtom);
    const fileRef = useRef(null);
    // pending: [{ id, file, previewUrl, caption, location, uploading }]
    const [pending, setPending] = useState([]);
    // Uploaded photos render as stacked cards ('list') or thumbnail tiles ('grid').
    const [viewMode, setViewMode] = useState('list');

    if (!report) return null;

    // report.photos also carries sub documents (Lab/Eq step); only true photos
    // belong on this step.
    const photos = (report.photos || []).filter((p) => !p.docType || p.docType === 'photo');

    const onPick = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        const items = files.map((file) => ({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            file,
            previewUrl: URL.createObjectURL(file),
            caption: '',
            location: '',
            noGps: false,
            uploading: false,
        }));
        setPending((p) => [...p, ...items]);
        // Pull GPS out of the EXIF before downscaling strips it; flag photos
        // that carry none so the user gets nudged to supply a location.
        items.forEach((item) => {
            readExifGps(item.file).then((gps) => {
                setPending((p) => p.map((x) => (
                    x.id === item.id
                        ? { ...x, location: x.location || gps || '', noGps: !gps }
                        : x
                )));
            });
        });
    };

    const setPendingField = (id, field, value) =>
        setPending((p) => p.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

    const uploadOne = async (item) => {
        setPendingField(item.id, 'uploading', true);
        try {
            const blob = await downscale(item.file);
            const res = await uploadFieldReportPhoto({
                reportId: report.reportId,
                buId: report.buId,
                caption: item.caption,
                location: item.location,
                uploadedBy: userName,
                contentType: 'image/jpeg',
                blob,
            });
            if (!res?.PhotoID) throw new Error(res?.error || 'Upload failed');
            setPending((p) => p.filter((x) => x.id !== item.id));
            URL.revokeObjectURL(item.previewUrl);
            setReport((r) => ({
                ...r,
                photos: [...(r.photos || []), {
                    photoId: res.PhotoID,
                    sasUrl: res.sasUrl,
                    docType: 'photo',
                    caption: item.caption,
                    location: item.location,
                }],
            }));
        } catch (err) {
            setPendingField(item.id, 'uploading', false);
            showToast('Photo upload failed — check connection and tap Upload to retry.', 'error');
        }
    };

    const removePending = (item) => {
        URL.revokeObjectURL(item.previewUrl);
        setPending((p) => p.filter((x) => x.id !== item.id));
    };

    const deleteUploaded = async (photo) => {
        try {
            await deleteFieldReportPhoto({ photoId: photo.photoId, updatedBy: userName });
            setReport((r) => ({ ...r, photos: (r.photos || []).filter((p) => p.photoId !== photo.photoId) }));
        } catch (err) {
            showToast('Photo delete failed — try again.', 'error');
        }
    };

    const photoCard = (children, key) => (
        <View key={key} style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 12, marginBottom: 12 }}>
            {children}
        </View>
    );

    return (
        <View style={{ paddingBottom: 8 }}>
            {!readOnly && (
                <>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        style={{ display: 'none' }}
                        onChange={onPick}
                    />
                    <Pressable
                        onPress={() => fileRef.current && fileRef.current.click()}
                        style={{ minHeight: 56, borderRadius: 10, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 14 }}
                    >
                        <Text style={{ fontSize: 15, fontWeight: '600', color: '#1D4ED8' }}>+ Take / Add Photos</Text>
                    </Pressable>
                </>
            )}

            {pending.map((item) =>
                photoCard(
                    <>
                        <img src={item.previewUrl} alt="" style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 8 }} />
                        <View style={{ marginTop: 10 }}>
                            <TextInput
                                value={item.caption}
                                placeholder="Caption — what is this photo?"
                                onChangeText={(t) => setPendingField(item.id, 'caption', t)}
                                style={{ ...inputBase, marginBottom: 8 }}
                            />
                            <TextInput
                                value={item.location}
                                placeholder="Location (MP / station / area)"
                                onChangeText={(t) => setPendingField(item.id, 'location', t)}
                                style={{ ...inputBase, marginBottom: item.noGps && !item.location ? 4 : 10 }}
                            />
                            {item.noGps && !item.location ? (
                                <Text style={{ fontSize: 11, color: '#B45309', marginBottom: 10 }}>
                                    No location found in this photo — turn on your camera's location services, or type where it was taken.
                                </Text>
                            ) : null}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                <Pressable onPress={() => removePending(item)} style={{ minHeight: 44, justifyContent: 'center', paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 14, color: '#DC2626', fontWeight: '600' }}>Discard</Text>
                                </Pressable>
                                <Pressable
                                    onPress={() => !item.uploading && uploadOne(item)}
                                    style={{ minHeight: 44, minWidth: 120, borderRadius: 10, backgroundColor: '#1D4ED8', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexDirection: 'row' }}
                                >
                                    {item.uploading ? <ActivityIndicator size="small" color="#fff" /> : null}
                                    <Text style={{ fontSize: 14, color: '#fff', fontWeight: '600', marginLeft: item.uploading ? 6 : 0 }}>
                                        {item.uploading ? 'Uploading…' : 'Upload'}
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    </>,
                    item.id,
                ),
            )}

            {!uploadOnly && photos.length > 0 && (
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 10 }}>
                    <View style={{ flexDirection: 'row', borderRadius: 8, border: '1px solid #CBD5E1', overflow: 'hidden' }}>
                        {['list', 'grid'].map((mode) => (
                            <Pressable
                                key={mode}
                                onPress={() => setViewMode(mode)}
                                style={{
                                    minHeight: 36,
                                    paddingLeft: 14,
                                    paddingRight: 14,
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: viewMode === mode ? '#1D4ED8' : '#fff',
                                }}
                            >
                                <Text style={{ fontSize: 13, fontWeight: '600', color: viewMode === mode ? '#fff' : '#475569' }}>
                                    {mode === 'list' ? 'List' : 'Grid'}
                                </Text>
                            </Pressable>
                        ))}
                    </View>
                </View>
            )}

            {!uploadOnly && viewMode === 'list' && photos.map((photo) =>
                photoCard(
                    <>
                        {photo.sasUrl ? (
                            <img src={photo.sasUrl} alt={photo.caption || ''} style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 8 }} />
                        ) : (
                            <View style={{ height: 80, borderRadius: 8, backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ fontSize: 12, color: '#64748B' }}>Photo uploaded</Text>
                            </View>
                        )}
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                            <View style={{ flex: 1, marginRight: 8 }}>
                                <Text style={{ fontSize: 13, fontWeight: '600', color: '#0F172A' }}>{photo.caption || 'No caption'}</Text>
                                {photo.location ? <Text style={{ fontSize: 12, color: '#64748B' }}>{photo.location}</Text> : null}
                            </View>
                            {!readOnly && (
                                <Pressable onPress={() => deleteUploaded(photo)} style={{ minHeight: 40, justifyContent: 'center', paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 13, color: '#DC2626', fontWeight: '600' }}>Delete</Text>
                                </Pressable>
                            )}
                        </View>
                    </>,
                    photo.photoId,
                ),
            )}

            {!uploadOnly && viewMode === 'grid' && photos.length > 0 && (
                <View style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 12 }}>
                    {photos.map((photo) => (
                        <View key={photo.photoId} style={{ borderRadius: 10, backgroundColor: '#fff', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                            {photo.sasUrl ? (
                                <img src={photo.sasUrl} alt={photo.caption || ''} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }} />
                            ) : (
                                <View style={{ width: '100%', aspectRatio: '1 / 1', backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center' }}>
                                    <Text style={{ fontSize: 12, color: '#64748B' }}>Photo uploaded</Text>
                                </View>
                            )}
                            <View style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 }}>
                                <Text numberOfLines={1} style={{ fontSize: 12, fontWeight: '600', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {photo.caption || 'No caption'}
                                </Text>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text numberOfLines={1} style={{ flex: 1, fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {photo.location || ''}
                                    </Text>
                                    {!readOnly && (
                                        <Pressable onPress={() => deleteUploaded(photo)} style={{ minHeight: 32, justifyContent: 'center', paddingLeft: 6, cursor: 'pointer' }}>
                                            <Text style={{ fontSize: 12, color: '#DC2626', fontWeight: '600' }}>Delete</Text>
                                        </Pressable>
                                    )}
                                </View>
                            </View>
                        </View>
                    ))}
                </View>
            )}

            {!uploadOnly && pending.length === 0 && photos.length === 0 && (
                <View style={{ borderRadius: 12, border: '1px dashed #CBD5E1', padding: 24, alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                    <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center' }}>
                        {readOnly ? 'No photos on this report.' : 'No photos yet. Progress shots, deliveries, delays, damage — anything worth a record.'}
                    </Text>
                </View>
            )}
        </View>
    );
};

export default PhotosStep;
